import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { UI_LANGUAGES } from "../i18n/languages";
import api from "../lib/api";
import { Globe, Search, Check } from "lucide-react";

export default function LanguageSwitcher({ dark = false }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [openUp, setOpenUp] = useState(false);
  const boxRef = useRef(null);

  const base = (i18n.language || "en").split("-")[0];
  const current = UI_LANGUAGES.find((l) => l.code === base) || UI_LANGUAGES.find((l) => l.code === "en");

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filtered = UI_LANGUAGES.filter((l) =>
    l.native.toLowerCase().includes(query.trim().toLowerCase()) || l.code.includes(query.trim().toLowerCase())
  );

  const pick = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
    setQuery("");
    // Mémorise la langue préférée côté serveur (emails localisés) si connecté
    api.patch("/auth/profile", { preferred_language: code }).catch(() => {});
  };

  return (
    <div className="relative" ref={boxRef} data-testid="language-switcher">
      <button type="button" data-testid="language-switcher-button"
        onClick={() => {
          if (!open && boxRef.current) {
            const r = boxRef.current.getBoundingClientRect();
            setOpenUp(window.innerHeight - r.bottom < 340);
          }
          setOpen((v) => !v);
        }}
        className={`inline-flex items-center gap-1.5 pl-3 pr-3 py-1.5 text-xs rounded-full outline-none transition-colors ${
          dark
            ? "bg-white/5 border border-white/10 text-white/90 hover:bg-white/10"
            : "bg-white border border-[#E2E5E0] text-[#1f2421] hover:border-[#1A4D2E]"
        }`}>
        <Globe className={`w-3.5 h-3.5 ${dark ? "text-white/60" : "text-[#6B726A]"}`} />
        {current.native}
      </button>

      {open && (
        <div className={`absolute z-[90] w-56 bg-white rounded-2xl border border-[#E2E5E0] shadow-xl overflow-hidden ${openUp ? "bottom-full mb-2" : "mt-2"}`}
          data-testid="language-switcher-panel">
          <div className="p-2 border-b border-[#E2E5E0]">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-[#6B726A]" />
              <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                data-testid="language-switcher-search"
                placeholder="Search…"
                className="w-full text-xs pl-8 pr-2 py-1.5 rounded-lg border border-[#E2E5E0] outline-none focus:border-[#1A4D2E] text-[#1f2421]" />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-[#6B726A]">—</div>
            ) : filtered.map((l) => (
              <button key={l.code} type="button" onClick={() => pick(l.code)}
                data-testid={`language-option-${l.code}`}
                className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between hover:bg-[#F7F8F6] ${
                  l.code === base ? "text-[#1A4D2E] font-semibold" : "text-[#1f2421]"
                }`}>
                {l.native}
                {l.code === base && <Check className="w-3.5 h-3.5" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
